import React, { useRef, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

interface PreviewAudioPlayerProps {
  audioUrl: string
  className?: string
  onFinished?: () => void
}

/**
 * Simple auto-playing audio player for voice message previews.
 * Automatically plays when the audioUrl is set and doesn't include download functionality.
 */
export const PreviewAudioPlayer: React.FC<PreviewAudioPlayerProps> = ({
  audioUrl,
  className = '',
  onFinished,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFinished, setIsFinished] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !audioUrl) return

    setIsLoading(true)
    setError(null)
    setIsFinished(false)

    let isCancelled = false

    // Auto-play when URL is set
    const playAudio = async () => {
      try {
        // Wait for audio to be ready
        if (audio.readyState < 2) {
          // HAVE_CURRENT_DATA
          await new Promise<void>((resolve, reject) => {
            const handleCanPlay = () => {
              audio.removeEventListener('canplay', handleCanPlay)
              audio.removeEventListener('error', handleLoadError)
              if (!isCancelled) {
                resolve()
              }
            }
            const handleLoadError = () => {
              audio.removeEventListener('canplay', handleCanPlay)
              audio.removeEventListener('error', handleLoadError)
              reject(new Error('Audio load error'))
            }
            audio.addEventListener('canplay', handleCanPlay)
            audio.addEventListener('error', handleLoadError)
          })
        }

        if (isCancelled) return

        await audio.play()
        if (!isCancelled) {
          setIsLoading(false)
        }
      } catch (err: any) {
        // AbortError is expected when audio is paused/reset - we can ignore it
        if (err?.name === 'AbortError' || err?.name === 'NotAllowedError') {
          // These are expected errors that we can safely ignore
          if (!isCancelled) {
            setIsLoading(false)
          }
          return
        }
        console.error('Autoplay failed:', err)
        if (!isCancelled) {
          setError('Failed to play preview')
          setIsLoading(false)
          setIsFinished(true)
        }
      }
    }

    // Reset and play when URL changes
    audio.pause()
    audio.currentTime = 0
    audio.load()
    playAudio()

    const handleEnded = () => {
      if (isCancelled) return
      setIsLoading(false)
      setIsFinished(true)
      // Call the onFinished callback to clear the preview URL in the parent
      if (onFinished) {
        // Small delay to ensure smooth transition
        setTimeout(() => {
          if (!isCancelled) {
            onFinished()
          }
        }, 500)
      }
    }

    const handleError = () => {
      if (isCancelled) return
      setError('Audio file could not be loaded')
      setIsLoading(false)
      setIsFinished(true)
    }

    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      isCancelled = true
      audio.pause()
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [audioUrl, onFinished])

  if (!audioUrl || isFinished) {
    return null
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <audio ref={audioRef} src={audioUrl} preload="auto" />
      {isLoading && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Playing preview...</span>
        </div>
      )}
      {!isLoading && !error && (
        <span className="text-muted-foreground text-sm">Preview playing...</span>
      )}
    </div>
  )
}

export default PreviewAudioPlayer
